import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useEmailSettings } from '../hooks/useEmailSettings';
import { COMPANY_LOCATION } from '../config/companyLocation';

interface LocalSEOSchemaProps {
  page?: 'home' | 'products' | 'student' | 'business' | 'about' | 'contact';
}

const LocalSEOSchema: React.FC<LocalSEOSchemaProps> = ({ page = 'home' }) => {
  const { settings } = useEmailSettings();

  // Format phone for schema (remove dashes and add +46 prefix if needed)
  const rawPhone = settings.company_phone || '';
  const formattedPhone = rawPhone
    ? (rawPhone.replace(/[-\s]/g, '').startsWith('+')
        ? rawPhone.replace(/[-\s]/g, '')
        : `+46${rawPhone.replace(/^0/, '').replace(/[-\s]/g, '')}`)
    : '';

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  const baseSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": settings.company_name || "Webbplats",
    "alternateName": settings.company_name || undefined,
    "description": "Professionell uthyrning av festutrustning i Skåne. Ljud, ljus och DJ-utrustning för alla tillfällen.",
    "url": baseUrl || undefined,
    "telephone": formattedPhone || undefined,
    "email": settings.company_email,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": COMPANY_LOCATION.street,
      "addressLocality": COMPANY_LOCATION.locality,
      "postalCode": COMPANY_LOCATION.postalCode,
      "addressRegion": COMPANY_LOCATION.region,
      "addressCountry": COMPANY_LOCATION.country
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": COMPANY_LOCATION.latitude,
      "longitude": COMPANY_LOCATION.longitude
    },
    "areaServed": [
      "Malmö", "Lund", "Helsingborg", "Kristianstad", "Landskrona", 
      "Trelleborg", "Ängelholm", "Hässleholm", "Ystad", "Eslöv",
      "Höganäs", "Kävlinge", "Staffanstorp", "Burlöv", "Vellinge",
      "Svedala", "Skurup", "Sjöbo", "Hörby", "Höör", "Tomelilla",
      "Bromölla", "Osby", "Perstorp", "Klippan", "Åstorp", "Båstad",
      "Bjuv", "Svalöv", "Simrishamn",
      "Näset", "Höllviken", "Ljunghusen", "Falsterbo", "Skanör"
    ],
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": COMPANY_LOCATION.latitude,
        "longitude": COMPANY_LOCATION.longitude
      },
      "geoRadius": 100000
    },
    "priceRange": "495-8490 SEK",
    "paymentAccepted": ["Credit Card", "Debit Card", "Swish", "Bank Transfer"],
    "currenciesAccepted": "SEK",
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "description": "Öppet efter överenskommelse"
    },
    "hasMap": COMPANY_LOCATION.mapsUrl,
    "image": baseUrl ? [baseUrl + "/logo.png",
      "https://images.pexels.com/photos/2747446/pexels-photo-2747446.jpeg?auto=compress&cs=tinysrgb&w=1200",
      "https://images.pexels.com/photos/164938/pexels-photo-164938.jpeg?auto=compress&cs=tinysrgb&w=1200"
    ] : [],
    "sameAs": [
      "https://www.facebook.com/festutrustning",
      "https://www.instagram.com/festutrustning"
    ]
  };

  // Page-specific schema additions
  const pageSchemas: Record<string, any> = {
    home: {
      ...baseSchema,
      "@type": ["LocalBusiness", "RentalBusiness"],
      "makesOffer": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Studentflak ljudsystem",
            "description": "Specialanpassade ljudsystem för studentflak"
          },
          "priceSpecification": {
            "@type": "PriceSpecification",
            "price": "4490-8490",
            "priceCurrency": "SEK",
            "unitText": "per dag",
            "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Företagsevent festutrustning",
            "description": "Professionell utrustning för företagsevent"
          },
          "priceSpecification": {
            "@type": "PriceSpecification",
            "price": "2495-6995",
            "priceCurrency": "SEK",
            "unitText": "per dag",
            "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Ljud och ljus uthyrning",
            "description": "Komplett ljud- och ljusutrustning för alla tillfällen"
          },
          "priceSpecification": {
            "@type": "PriceSpecification",
            "price": "495-8490",
            "priceCurrency": "SEK",
            "unitText": "per dag",
            "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          }
        }
      ],
      "slogan": "Professionell festutrustning för alla tillfällen",
      "keywords": "hyra festutrustning skåne, ljud och ljus uthyrning, studentflak ljud, företagsevent utrustning"
    },
    student: {
      ...baseSchema,
      "@type": "Service",
      "name": settings.company_name ? `Studentflak ljudsystem - ${settings.company_name}` : "Studentflak ljudsystem",
      "serviceType": "Studentflak ljuduthyrning",
      "provider": baseSchema,
      "slogan": "Maxad stämning för ert studentflak!",
      "keywords": "studentflak ljud, hyra ljudsystem student, studentfest utrustning, studentflak högtalare"
    },
    business: {
      ...baseSchema,
      "@type": "Service",
      "name": settings.company_name ? `Företagsevent - ${settings.company_name}` : "Företagsevent festutrustning",
      "serviceType": "Företagsevent uthyrning",
      "provider": baseSchema,
      "slogan": "Professionell festutrustning för minnesvärda företagsevent",
      "keywords": "företagsevent utrustning, konferens ljud och ljus, kickoff teknik, mässa ljudsystem"
    },
    products: {
      ...baseSchema,
      "@type": ["LocalBusiness", "RentalBusiness"],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": settings.company_name ? `${settings.company_name} produkter` : "Festutrustning produkter",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "Ljudpaket Basic",
              "description": "Perfekt för mindre fester med upp till 30-40 personer"
            },
            "price": "695",
            "priceCurrency": "SEK",
            "availability": "https://schema.org/InStock",
            "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "Karaokepaket Komplett",
              "description": "Professionellt karaokepaket med över 30,000 låtar"
            },
            "price": "895",
            "priceCurrency": "SEK",
            "availability": "https://schema.org/InStock",
            "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          }
        ]
      }
    }
  };

  const schema = pageSchemas[page] || baseSchema;

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export default LocalSEOSchema;